/**
 * Flush: Theme Web Components
 * Native browser APIs only, no external dependencies.
 */

/* --- Dialog motion ---
   A <dialog> appears and disappears in one style change. The stylesheets animate the entry
   with @starting-style; the exit needs script, and this works in every browser. close() adds
   is-closing, the stylesheet slides or fades the surface and its backdrop out, and the dialog
   really closes when the surface's transition ends (a timer slightly longer than the
   transition covers a transitionend that never comes). The close event, and everything each
   component hangs on it (scroll lock, focus, messages), still runs once, after the motion.
   Reduced motion, or a surface with no exit transition, closes at once. cancel() stops a
   close that is still running, so an open() that arrives meanwhile keeps the dialog open.
   Section scripts load before this file, so they reach it through window.DialogMotion when
   they close, and close at once without it. */
const DialogMotion = (() => {
  const closing = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // The surface's longest transform or opacity transition, delay included, in ms.
  const exitTime = (surface) => {
    const style = getComputedStyle(surface);
    const durations = style.transitionDuration.split(',');
    const delays = style.transitionDelay.split(',');
    let longest = 0;
    style.transitionProperty.split(',').forEach((name, i) => {
      if (!['transform', 'opacity', 'all'].includes(name.trim())) return;
      const seconds = parseFloat(durations[i % durations.length]) + parseFloat(delays[i % delays.length]);
      longest = Math.max(longest, seconds * 1000);
    });
    return longest;
  };

  const stop = (dialog) => {
    const state = closing.get(dialog);
    if (!state) return false;
    closing.delete(dialog);
    clearTimeout(state.timer);
    state.surface.removeEventListener('transitionend', state.onEnd);
    dialog.removeEventListener('close', state.onClose);
    dialog.classList.remove('is-closing');
    return true;
  };

  /**
   * @param {HTMLDialogElement} dialog
   * @param {{ surface?: Element, onClosed?: () => void }} [options] surface is the element
   *   whose exit transition sets the pace; onClosed runs once the dialog has closed.
   */
  const close = (dialog, { surface = dialog, onClosed } = {}) => {
    if (!dialog || !dialog.open || closing.has(dialog)) return;
    const shut = () => {
      if (dialog.open) dialog.close();
      if (onClosed) onClosed();
    };
    if (reducedMotion.matches) {
      shut();
      return;
    }
    dialog.classList.add('is-closing');
    // Reading the style here also starts the exit transition.
    const time = exitTime(surface);
    // theme.css cuts transitions to 0.01 ms for reduced motion; treat anything that short as none.
    if (time < 20) {
      dialog.classList.remove('is-closing');
      shut();
      return;
    }
    const finish = () => {
      stop(dialog);
      shut();
    };
    const state = {
      surface,
      // Only the surface's own transition: not the backdrop's (pseudoElement) and not a
      // transition bubbling up from something inside it.
      onEnd: (event) => {
        if (event.target === surface && !event.pseudoElement) finish();
      },
      // Something closed the dialog first (a second Escape the browser would not let the page
      // hold): drop the closing state and never close a dialog that has opened again since.
      onClose: () => {
        stop(dialog);
        if (onClosed) onClosed();
      },
      timer: setTimeout(finish, time + 80),
    };
    closing.set(dialog, state);
    surface.addEventListener('transitionend', state.onEnd);
    dialog.addEventListener('close', state.onClose);
  };

  return { close, cancel: stop };
})();
window.DialogMotion = DialogMotion;

/* --- Cart Drawer ---
   Intercepts add-to-cart forms, calls Shopify's cart AJAX API,
   then refreshes the drawer contents and slides it open.
   Uses the native <dialog> element for focus trap + Escape handling. */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-cart-drawer]');
    if (!this.dialog) return;
    // The panel's slide sets the pace of the animated close (DialogMotion).
    this.panel = this.querySelector('[data-cart-drawer-panel]') || this.dialog;

    // Every way the dialog closes (button, backdrop, Escape) ends up here once the panel has
    // slid out: release the page scroll and clear stale messages (M1.11).
    this.dialog.addEventListener('close', () => {
      document.body.style.overflow = '';
      this.#message(this.querySelector('[data-cart-drawer-error]'), '');
      this.#message(this.querySelector('[data-cart-drawer-status]'), '');
    });

    // Escape slides the panel out too. It does not call close(): focus goes back where the
    // browser puts it, as before, and to the trigger only when the browser has nowhere to put
    // it (Safari does not focus a clicked button, so focus would end on <body>). A request
    // the browser will not let the page hold closes at once.
    this.dialog.addEventListener('cancel', (event) => {
      if (!event.cancelable) return;
      event.preventDefault();
      DialogMotion.close(this.dialog, {
        surface: this.panel,
        onClosed: () => {
          const active = document.activeElement;
          if (!active || active === document.body || this.dialog.contains(active)) this.#returnFocus();
        },
      });
    });

    // Close triggers (close button, "continue shopping", backdrop click)
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-drawer-close]')) {
        this.close();
      }
      // Backdrop click: clicking on the dialog itself (not panel content) closes
      if (event.target === this.dialog) {
        this.close();
      }
    });

    // Qty +/- and remove
    this.addEventListener('click', (event) => {
      const inc = event.target.closest('[data-cart-drawer-increase]');
      const dec = event.target.closest('[data-cart-drawer-decrease]');
      const rem = event.target.closest('[data-cart-drawer-remove]');
      // Pass the action so focus can be restored to the matching control after the
      // drawer body is re-rendered (WCAG 2.4.3 / 3.2.2)
      if (inc) this.#changeQuantity(inc.dataset.lineKey, '+1', 'increase');
      if (dec) this.#changeQuantity(dec.dataset.lineKey, '-1', 'decrease');
      if (rem) this.#changeQuantity(rem.dataset.lineKey, 0, 'remove');
    });

    // Header / any external trigger to open
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-cart-drawer-open]');
      if (opener) {
        event.preventDefault();
        // Remember what opened us so focus can return there on close (WCAG 2.4.3)
        this.lastTrigger = opener;
        this.open();
      }
    });

    // Intercept add-to-cart form submits site-wide
    document.addEventListener('submit', this.#onAddToCart.bind(this));

    // Allow other scripts to force-refresh the drawer: window.dispatchEvent(new CustomEvent('cart:refresh'))
    window.addEventListener('cart:refresh', () => this.#refreshContents());
  }

  open() {
    if (!this.dialog) return;
    // An open that arrives while the panel is sliding out (an add finishing) stops the close:
    // the panel slides back and the dialog never closes, so focus, scroll lock and messages stay.
    DialogMotion.cancel(this.dialog);
    // M3.5: a second showModal() on an open modal throws in older Safari, which the add
    // handler would report as a failed add after a successful one.
    if (this.dialog.open) {
      this.#loadUpsell();
      return;
    }
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    document.body.style.overflow = 'hidden';
    this.#loadUpsell();
  }

  close() {
    if (!this.dialog) return;
    // Without native dialog support there is no close event and no motion: close and tidy
    // up here.
    if (typeof this.dialog.close !== 'function') {
      this.dialog.removeAttribute('open');
      document.body.style.overflow = '';
      this.#message(this.querySelector('[data-cart-drawer-error]'), '');
      this.#message(this.querySelector('[data-cart-drawer-status]'), '');
      this.#returnFocus();
      return;
    }
    // The panel slides out, then the dialog closes and its close event clears the scroll lock
    // and stale messages.
    DialogMotion.close(this.dialog, { surface: this.panel, onClosed: () => this.#returnFocus() });
  }

  // Return focus to whatever opened the drawer (WCAG 2.4.3) so it doesn't fall to <body>.
  #returnFocus() {
    if (this.lastTrigger && this.lastTrigger.isConnected) {
      this.lastTrigger.focus();
    }
  }

  /* M1.11 (F35): errors are shown inline and announced; the shopper is never sent to
     Shopify's plain error page. Text is set with textContent only. */
  #pendingMessages = new WeakMap();

  #message(el, text) {
    if (!el) return;
    cancelAnimationFrame(this.#pendingMessages.get(el));
    el.textContent = '';
    if (!text) return;
    // Clear, then set on the next frame, so a repeat of the same text is announced again.
    this.#pendingMessages.set(
      el,
      requestAnimationFrame(() => {
        el.textContent = text;
      })
    );
  }

  #errorText(json, fallback) {
    const text = json && (typeof json.description === 'string' ? json.description : json.message);
    return typeof text === 'string' && text.trim() ? text.trim() : fallback;
  }

  #formError(form, submitBtn) {
    let el = form.querySelector('[data-cart-error]');
    if (!el) {
      // App or third-party add forms get the same inline alert after their button.
      el = document.createElement('p');
      el.className = 'product-page__cart-error';
      el.setAttribute('role', 'alert');
      el.setAttribute('data-cart-error', '');
      if (submitBtn) submitBtn.insertAdjacentElement('afterend', el);
      else form.appendChild(el);
    }
    return el;
  }

  async #onAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = (form.getAttribute('action') || '').replace(/^\s+|\s+$/g, '');
    // Only intercept native Shopify add-to-cart forms
    if (!action.endsWith('/cart/add')) return;

    event.preventDefault();

    // M3.13: nothing chosen yet ("Choose a finish" state) means no variant: never send it.
    const idField = form.querySelector('[name="id"]');
    if (idField && !idField.value) return;

    // M3.1: the product form has two submit buttons, the in-flow one and the sticky
    // bar's (associated through form="ProductForm", so it sits outside the form in the
    // DOM). form.elements lists both. event.submitter tells which one was used; old
    // Safari lacks it, so fall back to the in-flow button.
    const submitters = Array.from(form.elements).filter((el) => el.type === 'submit');
    const submitBtn =
      event.submitter && event.submitter.form === form ? event.submitter : form.querySelector('[type="submit"]');
    // Guard double submit: a request is already in flight from either button.
    if (submitters.some((btn) => btn.dataset.cartBusy === 'true')) return;
    // Busy state uses aria-disabled, not the disabled property: disabling the focused
    // button would blur it to <body> and a keyboard user would lose their place (WCAG 2.4.3).
    // Every submitter of the form is marked, so a tap on the other button is ignored.
    const hadFocus = submitBtn != null && document.activeElement === submitBtn;
    // M3.5: an add from inside the drawer (complete the set) loses its form on re-render.
    const addedInDrawer = this.contains(form);
    for (const btn of submitters) {
      btn.setAttribute('aria-disabled', 'true');
      btn.dataset.cartBusy = 'true';
    }
    // The error goes next to the button that was used: the sticky bar has its own alert.
    const stickyBar = submitBtn ? submitBtn.closest('[data-sticky-atc]') : null;
    const stickyErrors = submitters
      .map((btn) => btn.closest('[data-sticky-atc]'))
      .filter(Boolean)
      .map((bar) => bar.querySelector('[data-cart-error]'));
    const formErrorEl = this.#formError(form, submitBtn && !stickyBar ? submitBtn : form.querySelector('[type="submit"]'));
    const errorEl = stickyBar ? stickyBar.querySelector('[data-cart-error]') || formErrorEl : formErrorEl;
    this.#message(formErrorEl, '');
    for (const el of stickyErrors) this.#message(el, '');
    // A success message from an earlier add must not sit in the dialog next to a failure.
    this.#message(this.querySelector('[data-cart-drawer-status]'), '');

    try {
      const formData = new FormData(form);
      formData.append('sections', this.#sectionId());
      formData.append('sections_url', `${window.location.pathname}${window.location.search}`);
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        console.error('Add to cart failed', err);
        // M3.6: gift card recipient errors come back as a per-field object (errors, or
        // description on some responses). Hand it to the form's recipient fields; the
        // general alert gets a theme sentence pointing at them, never the API's generic
        // message ("Cart Error") or "[object Object]". Only forms that carry the recipient
        // fieldset take this branch; any other object-shaped error falls through to the
        // plain message below.
        const fieldErrors = form.querySelector('gift-card-recipient')
          ? [err?.errors, err?.description].find((v) => v && typeof v === 'object')
          : null;
        if (fieldErrors) {
          form.dispatchEvent(new CustomEvent('gift-card-recipient:errors', { detail: { errors: fieldErrors } }));
          this.#message(errorEl, this.dataset.errorRecipient || this.dataset.errorAdd);
          return;
        }
        this.#message(errorEl, this.#errorText(err, this.dataset.errorAdd));
        return;
      }
      const data = await res.json();
      this.#message(this.querySelector('[data-cart-drawer-error]'), '');
      this.#applySectionHtml(data.sections);
      await this.#refreshContents();
      this.open();
      // Focus the new line so a keyboard user is not dropped to the top of the page.
      if (addedInDrawer) this.#restoreLineFocus(data.key, 'increase');
      const title = data.product_title || (data.items && data.items[0] && data.items[0].product_title);
      const added = title && this.dataset.added ? this.dataset.added.replace('[title]', () => title) : this.dataset.addedGeneric;
      this.#message(this.querySelector('[data-cart-drawer-status]'), added || '');
    } catch (e) {
      console.error('Add to cart error', e);
      this.#message(errorEl, this.dataset.errorAdd);
    } finally {
      for (const btn of submitters) {
        btn.removeAttribute('aria-disabled');
        delete btn.dataset.cartBusy;
      }
      if (submitBtn) {
        // If focus still fell to <body> (for example, the drawer did not open), put it back.
        const active = document.activeElement;
        if (hadFocus && submitBtn.isConnected && (!active || active === document.body)) {
          submitBtn.focus();
        }
      }
    }
  }

  async #changeQuantity(lineKey, delta, action) {
    if (!lineKey) return;
    // Ignore clicks while a change is pending (stops double +1 races).
    if (this.changing) return;
    this.changing = true;
    const errorEl = this.querySelector('[data-cart-drawer-error]');
    // The "added to your cart" status is stale once the shopper edits the cart.
    this.#message(this.querySelector('[data-cart-drawer-status]'), '');
    let removing = null;
    try {
      const currentItem = this.querySelector(`[data-line-key="${CSS.escape(lineKey)}"] [data-line-qty]`);
      const currentQty = currentItem ? parseInt(currentItem.textContent, 10) : 1;
      const lineTitle = this.querySelector(`[data-line-key="${CSS.escape(lineKey)}"] .v2-cart-line__title`)?.textContent.trim() || '';
      let newQty;
      if (delta === '+1') newQty = currentQty + 1;
      else if (delta === '-1') newQty = Math.max(0, currentQty - 1);
      else newQty = delta;
      // A line on its way out fades back while the request runs (assets/v2-cart.css). The
      // re-render drops it; a failed request brings it back (finally below).
      if (newQty === 0) {
        removing = this.querySelector(`[data-cart-drawer-item][data-line-key="${CSS.escape(lineKey)}"]`);
        removing?.classList.add('is-removing');
      }

      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: newQty,
          sections: [this.#sectionId()],
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        console.error('Cart change failed', err);
        // Leave the drawer body as it is; focus stays on the control that was used.
        this.#message(errorEl, this.#errorText(err, this.dataset.errorChange));
        return;
      }
      const data = await res.json();
      const line = (data.items || []).find((item) => item.key === lineKey);
      if (newQty > 0 && line && line.quantity < newQty && this.dataset.errorMax) {
        // Shopify capped the quantity at what is in stock.
        this.#message(errorEl, this.dataset.errorMax.replace('[quantity]', String(line.quantity)));
      } else {
        this.#message(errorEl, '');
      }
      this.#setCartCount(data.item_count);
      this.#applySectionHtml(data.sections);
      this.#restoreLineFocus(lineKey, action);
      // WCAG 4.1.3: focus stays on the same control, so say what changed.
      const status = this.querySelector('[data-cart-drawer-status]');
      if (lineTitle && this.dataset.quantityUpdated && this.dataset.removed) {
        const text = newQty > 0 && line
          ? this.dataset.quantityUpdated.replace('[title]', () => lineTitle).replace('[quantity]', () => String(line.quantity))
          : this.dataset.removed.replace('[title]', () => lineTitle);
        this.#message(status, text);
      }
    } catch (e) {
      console.error('Quantity change error', e);
      this.#message(errorEl, this.dataset.errorChange);
    } finally {
      removing?.classList.remove('is-removing');
      this.changing = false;
    }
  }

  #sectionId() {
    const wrap = this.closest('.shopify-section');
    return wrap?.id?.replace(/^shopify-section-/, '') || 'cart-drawer';
  }

  #setCartCount(count) {
    const value = count == null ? '' : String(count);
    for (const bubble of document.querySelectorAll('[data-cart-count-bubble]')) {
      bubble.textContent = value;
    }
    // v2 header: the phone badge hides at zero (assets/v2-header.css reads data-count).
    for (const wrap of document.querySelectorAll('[data-cart-count]')) {
      wrap.dataset.count = value;
    }
    // RET-259: the drawer title reads "Cart (3)" and shows no count for an empty cart.
    const titleCount = this.querySelector('[data-cart-drawer-count]');
    if (titleCount) titleCount.textContent = Number(count) > 0 ? `(${Number(count)})` : '';
  }

  #applySectionHtml(sections) {
    if (!sections) return;
    const html = sections[this.#sectionId()] || Object.values(sections)[0];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const freshBody = doc.querySelector('[data-cart-drawer-body]');
    const currentBody = this.querySelector('[data-cart-drawer-body]');
    if (freshBody && currentBody) {
      currentBody.replaceChildren(...freshBody.childNodes);
    }
    if (this.dialog?.open) this.#loadUpsell();
  }

  /* M3.5: complete the set. Runs only while the drawer is open, so a closed drawer costs
     no request. Complementary picks for the most recent line, then related; the section
     (sections/cart-upsell.liquid) applies the in-stock, not-in-cart and one-tap rules.
     Results are cached per anchor and cart contents; the previous request is aborted and a
     result for a placeholder that has been replaced is dropped. Failures stay silent. */
  #upsellCache = new Map();

  async #loadUpsell() {
    const slot = this.querySelector('[data-cart-upsell]');
    if (!slot || !slot.dataset.anchor || !slot.dataset.url) return;
    const key = `${slot.dataset.anchor}|${slot.dataset.exclude || ''}`;
    if (this.#upsellCache.has(key)) {
      this.#fillUpsell(slot, this.#upsellCache.get(key));
      return;
    }
    this.upsellController?.abort();
    const controller = new AbortController();
    this.upsellController = controller;
    const base = `${slot.dataset.url}?section_id=cart-upsell&product_id=${encodeURIComponent(slot.dataset.anchor)}&limit=6`;
    try {
      let nodes = await ProductRecommendations.fetchItems(`${base}&intent=complementary`, controller.signal, '.cart-upsell__results');
      if (!nodes[0]?.querySelector('[data-upsell-item]')) {
        nodes = await ProductRecommendations.fetchItems(`${base}&intent=related`, controller.signal, '.cart-upsell__results');
      }
      if (controller.signal.aborted || !slot.isConnected) return;
      const results = nodes[0] && nodes[0].querySelector('[data-upsell-item]') ? nodes[0] : null;
      if (this.#upsellCache.size >= 10) this.#upsellCache.delete(this.#upsellCache.keys().next().value);
      this.#upsellCache.set(key, results);
      this.#fillUpsell(slot, results);
    } catch (e) {
      console.warn('Complete the set did not load', e);
    }
  }

  #fillUpsell(slot, results) {
    if (!slot.isConnected) return;
    if (results) slot.replaceChildren(document.importNode(results, true));
    else slot.replaceChildren();
  }

  // After a drawer refresh, return keyboard focus to the control the user just used on
  // the same line. Falls back to nearby controls if the exact one is gone (line removed,
  // or decrement that hit zero and removed the line).
  #restoreLineFocus(lineKey, action) {
    if (!action) return;
    // A control we can actually move focus to. Disabled buttons can't receive focus
    // (e.g. the decrease button becomes disabled once a line drops to quantity 1).
    const firstFocusable = (root) => {
      if (!root) return null;
      for (const el of root.querySelectorAll('[data-cart-drawer-increase], [data-cart-drawer-decrease], [data-cart-drawer-remove]')) {
        if (!el.disabled) return el;
      }
      return null;
    };

    const line = this.querySelector(`[data-line-key="${CSS.escape(lineKey)}"]`);
    if (line) {
      const selectorByAction = {
        increase: '[data-cart-drawer-increase]',
        decrease: '[data-cart-drawer-decrease]',
        remove: '[data-cart-drawer-remove]',
      };
      // Prefer the same control; if it's gone or now disabled, fall back to another
      // enabled control on the same line.
      const same = line.querySelector(selectorByAction[action]);
      const target = same && !same.disabled ? same : firstFocusable(line);
      if (target) {
        target.focus();
        return;
      }
    }
    // The line is gone (removed, or quantity reached zero). Focus a sensible nearby element:
    // the first remaining line control, otherwise the close button, otherwise the drawer.
    const fallback =
      firstFocusable(this.querySelector('[data-cart-drawer-body]')) ||
      this.querySelector('[data-cart-drawer-close]') ||
      this.dialog;
    fallback?.focus();
  }

  async #refreshContents() {
    try {
      const sectionId = this.#sectionId();
      const sectionUrl = new URL(window.location.href);
      sectionUrl.searchParams.set('sections', sectionId);
      const [cartRes, sectionRes] = await Promise.all([
        fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }),
        fetch(sectionUrl.toString(), {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        }),
      ]);
      if (!cartRes.ok || !sectionRes.ok) throw new Error('Drawer refresh failed');
      const cart = await cartRes.json();
      const sections = await sectionRes.json();
      this.#setCartCount(cart.item_count);
      this.#applySectionHtml(sections);
    } catch (e) {
      console.error('Drawer refresh error', e);
      this.#message(this.querySelector('[data-cart-drawer-error]'), this.dataset.errorRefresh);
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/* Product card quick add: the form posts to /cart/add, so CartDrawer adds the item, shows
   its busy state and opens. The drawer only remembers the controls it opens itself, so note
   the card's button here and focus goes back to it when the drawer closes. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-quick-add] button[type="submit"]');
  if (!button) return;
  const drawer = document.querySelector('cart-drawer');
  if (drawer) drawer.lastTrigger = button;
});


/* --- Announcement Bar ---
   M1.12: rotates message blocks every data-speed ms. Pauses on hover, on focus
   inside the bar, while the tab is hidden, and when the shopper presses the pause
   control (WCAG 2.2.2). No live region: rotating text is not announced. Inactive
   messages are visibility:hidden, so only the active one is in the a11y tree. */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.dismissBtn = this.querySelector('[data-dismiss-announcement]');
    this.bar = this.querySelector('.announcement-bar');
    // Keyed per section instance (RET-132) so multiple stacked announcement bars
    // dismiss independently instead of sharing one flag.
    this.storageKey = `announcement-dismissed-${this.id || 'default'}`;

    let dismissed = false;
    try {
      dismissed = sessionStorage.getItem(this.storageKey) === '1';
    } catch (e) {
      dismissed = false;
    }
    if (dismissed && this.bar) {
      this.bar.hidden = true;
    }

    this.dismissBtn?.addEventListener('click', this.#dismiss.bind(this));
    this.#setupRotation();
  }

  disconnectedCallback() {
    this.#stop();
    this.abort?.abort();
  }

  #setupRotation() {
    this.messages = [...this.querySelectorAll('[data-announcement-message]')];
    if (this.messages.length < 2) return;
    this.speed = parseInt(this.dataset.speed, 10) || 6000;
    this.toggle = this.querySelector('[data-announcement-toggle]');
    this.userPaused = false;
    this.hovered = false;
    this.focused = false;
    this.editorHold = false;
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.addEventListener('mouseenter', () => { this.hovered = true; this.#sync(); }, { signal });
    this.addEventListener('mouseleave', () => { this.hovered = false; this.#sync(); }, { signal });
    this.addEventListener('focusin', () => { this.focused = true; this.#sync(); }, { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.focused = false;
        this.#sync();
      }
    }, { signal });
    document.addEventListener('visibilitychange', () => this.#sync(), { signal });

    this.toggle?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      const label = this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause;
      if (label) this.toggle.setAttribute('aria-label', label);
      this.#sync();
    }, { signal });

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.messages.indexOf(event.target);
        if (index === -1) return;
        this.editorHold = true;
        this.#show(index);
        this.#sync();
      }, { signal });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (!this.messages.includes(event.target)) return;
        this.editorHold = false;
        this.#sync();
      }, { signal });
    }

    this.#sync();
  }

  #shouldRun() {
    return !this.userPaused && !this.hovered && !this.focused && !this.editorHold && document.visibilityState !== 'hidden';
  }

  #sync() {
    if (this.#shouldRun()) this.#start();
    else this.#stop();
  }

  #start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.#next(), this.speed);
  }

  #stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  #next() {
    const current = this.messages.findIndex((m) => m.classList.contains('is-active'));
    this.#show((current + 1) % this.messages.length);
  }

  #show(index) {
    this.messages.forEach((m, i) => m.classList.toggle('is-active', i === index));
  }

  #dismiss() {
    if (this.bar) {
      this.bar.hidden = true;
      try {
        sessionStorage.setItem(this.storageKey, '1');
      } catch (e) {
        /* storage blocked: the bar stays hidden for this page view only */
      }
      this.#stop();
    }
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* --- Header --- */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.mobileToggle = this.querySelector('[data-mobile-menu-toggle]');
    this.mobileClose = this.querySelector('[data-mobile-menu-close]');
    this.mobileNav = this.querySelector('#MobileNav');

    this.mobileAbort?.abort();
    this.mobileAbort = new AbortController();
    const { signal } = this.mobileAbort;

    this.mobileToggle?.addEventListener('click', () => this.#openMobile(), { signal });
    this.mobileClose?.addEventListener('click', () => this.#dismissMobile(), { signal });
    // Escape slides the menu out like the close button. A request the browser will not let
    // the page hold closes at once.
    this.mobileNav?.addEventListener('cancel', (event) => {
      if (!event.cancelable) return;
      event.preventDefault();
      this.#dismissMobile();
    }, { signal });
    // Keep toggle state (and focus/overflow) in sync however the dialog closes, close button
    // or Escape (WCAG 4.1.2). Idempotent with #closeMobile.
    this.mobileNav?.addEventListener('close', () => this.#closeMobile(), { signal });
    this.mobileNav?.addEventListener('click', (event) => {
      // v2 drill-down: a row with children opens its panel; Back returns to the parent.
      const opener = event.target.closest('[data-mobile-nav-open]');
      if (opener) {
        this.#showPanel(opener);
        return;
      }
      const back = event.target.closest('[data-mobile-nav-back]');
      if (back) this.#hidePanel(back.closest('[data-mobile-nav-panel]'));
    }, { signal });

    this.#setupDropdowns();
    this.#setupTileWarmup();
  }

  disconnectedCallback() {
    this.mobileAbort?.abort();
    this.dropdownAbort?.abort();
    this.warmAbort?.abort();
  }

  /* M4.3: menu photos are lazy, and a lazy image inside a closed panel or drawer is not
     fetched until it shows, so the first open painted empty cards. Warm only the surface
     that is about to open, and only on intent, so nothing extra downloads on page load:
     - desktop: when the pointer enters, presses or focus reaches a nav item with a
       dropdown, switch that panel's images to eager (an eager image loads while hidden);
     - phone: when the menu toggle is pressed or clicked, switch the menu's images. */
  #setupTileWarmup() {
    this.warmAbort?.abort();
    this.warmAbort = new AbortController();
    const { signal } = this.warmAbort;
    const eager = (root) => {
      for (const img of root.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
    };

    for (const panel of this.querySelectorAll('.header__dropdown')) {
      if (!panel.querySelector('img[loading="lazy"]')) continue;
      const item = panel.closest('.header__nav-item') || panel;
      const warm = () => {
        eager(panel);
        for (const type of ['pointerenter', 'pointerdown', 'focusin']) item.removeEventListener(type, warm);
      };
      for (const type of ['pointerenter', 'pointerdown', 'focusin']) {
        item.addEventListener(type, warm, { signal, passive: true });
      }
    }

    if (this.mobileToggle && this.mobileNav) {
      const drawer = this.mobileNav;
      const toggle = this.mobileToggle;
      const warm = () => {
        eager(drawer);
        for (const type of ['pointerdown', 'click']) toggle.removeEventListener(type, warm);
      };
      for (const type of ['pointerdown', 'click']) {
        toggle.addEventListener(type, warm, { signal, passive: true });
      }
    }
  }

  /* M1.21: each desktop dropdown is a disclosure. The button toggles it on click (Enter
     and Space included); Escape closes it and returns focus to the button; tabbing out or
     clicking outside closes it. A close while the pointer is over the item adds
     is-dismissed so the resting mouse does not reopen it; mouseleave clears that. A close
     with the pointer elsewhere (outside click, Tab out) does not add it, since no
     mouseleave would follow to clear it and the next hover would fail.
     Mouse users get hover intent (fine pointers only, never touch): resting on an item
     opens its panel after OPEN_DELAY; while a panel is up, moving onto another item swaps
     straight to it (menu aim below holds it while the pointer heads down into the open
     panel); resting on a link with no panel, or leaving the nav row (the panels sit inside
     it), closes it after CLOSE_DELAY unless the pointer comes back. Hover intent leaves a
     panel alone while keyboard focus is in it. The panels share one white sheet
     (.header::after) sized to the open panel, so a swap only crossfades the content
     (assets/v2-header.css). */
  #setupDropdowns() {
    this.dropdownAbort?.abort();
    this.dropdownAbort = new AbortController();
    const { signal } = this.dropdownAbort;
    const header = this.querySelector('.header');
    const list = this.querySelector('.header__nav-list');
    const items = [...this.querySelectorAll('.header__nav-item--has-dropdown')];
    if (!header || !list || !items.length) return;

    const OPEN_DELAY = 120;
    const CLOSE_DELAY = 250;
    const AIM_REST = 100;
    // v2-header.css: a panel fades out in 160 ms; a swap eases the sheet's height in 220 ms.
    const FADE_OUT = 160;
    const SWAP = 240;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const byMouse = (event) => event.pointerType === 'mouse' && finePointer.matches;
    const hideTimers = new Map();
    let openTimer = 0;
    let closeTimer = 0;
    let aimTimer = 0;
    let swapTimer = 0;
    let aimItem = null;
    // Set by Tab or a keyboard press on a toggle, cleared by any pointer press.
    let keyboard = false;
    signal.addEventListener('abort', () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      clearTimeout(aimTimer);
      clearTimeout(swapTimer);
      for (const timer of hideTimers.values()) clearTimeout(timer);
      header.classList.remove('is-switching');
    });

    const toggleOf = (item) => item.querySelector('[data-dropdown-toggle]');
    const panelOf = (item) => item.querySelector('.header__dropdown');
    const openItem = () => items.find((item) => item.classList.contains('is-open'));
    // Keyboard focus inside a panel would drop to the page if the pointer closed it.
    const keyboardIn = (item) => keyboard && Boolean(item && panelOf(item)?.contains(document.activeElement));

    // Menu aim: the panels sit right under the nav row, so while one is open a pointer
    // moving down is on its way into it. An item it crosses on the way (the next button
    // along, going from an item on the right to a list on the left) waits for the pointer
    // to rest or turn before taking over; moving along the row swaps straight away.
    const trail = [];
    list.addEventListener('pointermove', (event) => {
      trail.push({ x: event.clientX, y: event.clientY, t: event.timeStamp });
      if (trail.length > 8) trail.shift();
    }, { signal, passive: true });
    const headingDown = (event) => {
      const from = trail.find((point) => event.timeStamp - point.t <= 80);
      if (!from) return false;
      const dy = event.clientY - from.y;
      return dy > 1 && dy >= Math.abs(event.clientX - from.x) * 0.1;
    };

    // The sheet takes the open panel's height (reading it lays out a panel that has just
    // been drawn, so its fade starts from the closed state). Only a change is written, since
    // the header group's MutationObserver measures again on every style write.
    const fitSheet = (item = openItem()) => {
      const panel = item && panelOf(item);
      if (!panel) return;
      const height = `${panel.offsetHeight}px`;
      if (header.style.getPropertyValue('--header-dropdown-h') === height) return;
      header.style.setProperty('--header-dropdown-h', height);
    };

    const mark = (item, open) => {
      item.classList.toggle('is-open', open);
      toggleOf(item)?.setAttribute('aria-expanded', String(open));
      item.classList.toggle('is-dismissed', !open && item.matches(':hover'));
      // A panel that is fading out is still drawn: inert keeps it out of the tab order
      // and the accessibility tree, and lets the pointer through.
      const panel = panelOf(item);
      if (panel) panel.inert = !open;
    };

    // is-shown keeps a closing panel drawn for its fade, then it goes back to display: none.
    const close = (item) => {
      mark(item, false);
      if (!item.classList.contains('is-shown') || hideTimers.has(item)) return;
      hideTimers.set(item, setTimeout(() => {
        hideTimers.delete(item);
        if (!item.classList.contains('is-open')) item.classList.remove('is-shown');
      }, FADE_OUT));
    };

    // via 'hover' marks a panel the pointer opened, which shows no chevron (the Header-Shop board
    // draws only the underline); a click, Enter or Space opening shows it as before.
    const setOpen = (item, open, via) => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      clearTimeout(aimTimer);
      aimItem = null;
      if (!open) {
        close(item);
        return;
      }
      if (item.classList.contains('is-open')) {
        if (via !== 'hover') item.classList.remove('is-hover-open');
        return;
      }
      const from = openItem();
      // With the sheet still up (a panel open or fading out) this is a swap: the sheet
      // stays and eases to the new height while the panels crossfade on it.
      if (items.some((other) => other.classList.contains('is-shown'))) {
        header.classList.add('is-switching');
        clearTimeout(swapTimer);
        swapTimer = setTimeout(() => header.classList.remove('is-switching'), SWAP);
      }
      clearTimeout(hideTimers.get(item));
      hideTimers.delete(item);
      item.classList.add('is-shown');
      item.classList.toggle('is-hover-open', via === 'hover');
      fitSheet(item);
      mark(item, true);
      if (from) close(from);
    };

    for (const item of items) item.classList.toggle('is-shown', item.classList.contains('is-open'));

    for (const item of items) {
      const toggle = toggleOf(item);
      toggle?.addEventListener('click', (event) => {
        // detail is 0 for a click from Enter, Space or a screen reader.
        if (event.detail === 0) keyboard = true;
        setOpen(item, !item.classList.contains('is-open'));
      }, { signal });

      item.addEventListener('focusout', (event) => {
        if (item.classList.contains('is-open') && !item.contains(event.relatedTarget)) setOpen(item, false);
      }, { signal });

      item.addEventListener('mouseleave', () => item.classList.remove('is-dismissed'), { signal });

      item.addEventListener('pointerenter', (event) => {
        if (!byMouse(event)) return;
        clearTimeout(openTimer);
        clearTimeout(aimTimer);
        aimItem = null;
        if (item.classList.contains('is-open')) {
          clearTimeout(closeTimer);
          return;
        }
        if (item.classList.contains('is-dismissed') || keyboardIn(openItem())) return;
        if (openItem() && headingDown(event)) {
          aimItem = item;
          aimTimer = setTimeout(() => setOpen(item, true, 'hover'), AIM_REST);
        } else if (openItem() || item.classList.contains('is-shown')) {
          // Swap straight to this panel while one is up, or bring it back if it is still fading.
          setOpen(item, true, 'hover');
        } else {
          openTimer = setTimeout(() => setOpen(item, true, 'hover'), OPEN_DELAY);
        }
      }, { signal });

      // A swap held back for menu aim goes ahead as soon as the pointer turns or rests.
      item.addEventListener('pointermove', (event) => {
        if (aimItem !== item || !byMouse(event)) return;
        clearTimeout(aimTimer);
        if (headingDown(event)) aimTimer = setTimeout(() => setOpen(item, true, 'hover'), AIM_REST);
        else setOpen(item, true, 'hover');
      }, { signal, passive: true });

      item.addEventListener('pointerleave', (event) => {
        if (!byMouse(event)) return;
        clearTimeout(openTimer);
        clearTimeout(aimTimer);
        aimItem = null;
      }, { signal });
    }

    // The grace period lets the pointer cut a corner on its way down into the panel, or
    // cross the gaps and a plain link to the next item, without the panel closing. On a
    // plain link it runs from the last movement, so only resting there closes the panel.
    const closeSoon = (event) => {
      const item = openItem();
      if (!byMouse(event) || !item || keyboardIn(item)) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => setOpen(item, false), CLOSE_DELAY);
    };
    list.addEventListener('pointerleave', closeSoon, { signal });
    for (const link of list.querySelectorAll('.header__nav-item:not(.header__nav-item--has-dropdown)')) {
      link.addEventListener('pointerenter', closeSoon, { signal });
      link.addEventListener('pointermove', closeSoon, { signal, passive: true });
    }

    document.addEventListener('pointerdown', () => {
      keyboard = false;
    }, { signal, passive: true });

    // Keep the sheet fitted when the open panel changes size (window resize, late fonts).
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => fitSheet());
      for (const item of items) {
        const panel = panelOf(item);
        if (panel) observer.observe(panel);
      }
      signal.addEventListener('abort', () => observer.disconnect());
    }

    // Back from the next page (back/forward cache): drop the panel the shopper clicked
    // through, since the pointer is no longer where it was.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && openItem()) setOpen(openItem(), false);
    }, { signal });

    // Escape closes an open or hovered dropdown wherever focus is (WCAG 1.4.13), and returns
    // focus to its toggle only when focus was inside it.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') keyboard = true;
      if (event.key !== 'Escape') return;
      const item = openItem() || items.find((i) => i.matches(':hover'));
      if (!item) return;
      const focusInside = item.contains(document.activeElement);
      setOpen(item, false);
      if (focusInside) toggleOf(item)?.focus();
    }, { signal });

    document.addEventListener('click', (event) => {
      if (event.target.closest('.header__nav')) return;
      for (const item of items) {
        if (item.classList.contains('is-open')) setOpen(item, false);
      }
    }, { signal });
  }

  #openMobile() {
    if (!this.mobileNav) return;
    // Opening while the menu slides out keeps it open.
    if (this.mobileNav.open) {
      DialogMotion.cancel(this.mobileNav);
      return;
    }
    this.#resetPanels();
    this.mobileNav.showModal();
    this.mobileToggle?.setAttribute('aria-expanded', 'true');
    if (this.mobileToggle?.dataset.labelClose) this.mobileToggle.setAttribute('aria-label', this.mobileToggle.dataset.labelClose);
    document.body.style.overflow = 'hidden';
  }

  // The menu slides out first (DialogMotion); its close event then runs #closeMobile.
  #dismissMobile() {
    if (this.mobileNav?.open) DialogMotion.close(this.mobileNav);
    else this.#closeMobile();
  }

  #closeMobile() {
    if (this.mobileNav?.open) this.mobileNav.close();
    this.mobileToggle?.setAttribute('aria-expanded', 'false');
    if (this.mobileToggle?.dataset.labelOpen) this.mobileToggle.setAttribute('aria-label', this.mobileToggle.dataset.labelOpen);
    document.body.style.overflow = '';
    this.mobileToggle?.focus();
  }

  /* v2 phone menu: one view shows at a time. Opening a panel hides the view that holds
     its row and moves focus to the panel's Back button; Back does the reverse and puts
     focus on the row again. The menu reopens on its top level. */
  #showPanel(opener) {
    const panel = this.mobileNav?.querySelector(`#${CSS.escape(opener.getAttribute('aria-controls'))}`);
    const from = opener.closest('[data-mobile-nav-view]');
    if (!panel || !from) return;
    from.hidden = true;
    panel.hidden = false;
    opener.setAttribute('aria-expanded', 'true');
    this.mobileNav.scrollTop = 0;
    panel.querySelector('[data-mobile-nav-back]')?.focus();
  }

  #hidePanel(panel) {
    if (!panel) return;
    const opener = this.mobileNav?.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`);
    const parent = opener?.closest('[data-mobile-nav-view]');
    panel.hidden = true;
    if (parent) parent.hidden = false;
    opener?.setAttribute('aria-expanded', 'false');
    this.mobileNav.scrollTop = 0;
    opener?.focus();
  }

  #resetPanels() {
    if (!this.mobileNav) return;
    for (const view of this.mobileNav.querySelectorAll('[data-mobile-nav-view]')) {
      view.hidden = view.hasAttribute('data-mobile-nav-panel');
    }
    for (const opener of this.mobileNav.querySelectorAll('[data-mobile-nav-open]')) {
      opener.setAttribute('aria-expanded', 'false');
    }
  }
}
customElements.define('site-header', SiteHeader);

/* --- Gift card recipient (M3.6) ---
   Shopify's native recipient form. The checkbox and fieldset render disabled; this
   enables the checkbox, and the fields only while it is ticked, so nothing is sent for
   an unticked add. Server per-field errors arrive from CartDrawer as a
   gift-card-recipient:errors event on the form. */
class GiftCardRecipient extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-recipient-toggle]');
    this.fields = this.querySelector('[data-recipient-fields]');
    if (!this.toggle || !this.fields) return;
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.toggle.disabled = false;
    const offset = this.querySelector('[data-recipient-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset());

    // Local dates (not toISOString, which shifts to UTC): today to 90 days ahead.
    const sendOn = this.#field('send_on');
    if (sendOn) {
      const today = new Date();
      const max = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 90);
      sendOn.min = this.#ymd(today);
      sendOn.max = this.#ymd(max);
      sendOn.addEventListener('invalid', () => this.#setError('send_on', this.dataset.errorSendOn), { signal });
    }
    this.#field('email')?.addEventListener('invalid', () => this.#setError('email', this.dataset.errorEmail), { signal });

    this.toggle.addEventListener('change', () => this.#sync(true), { signal });
    this.#sync(false);

    const message = this.#field('message');
    const count = this.querySelector('[data-message-count]');
    const live = this.querySelector('[data-message-live]');
    if (message && count) {
      let wasLow = false;
      const update = () => {
        const remaining = Math.max(0, (Number(message.maxLength) || 200) - message.value.length);
        const text = (count.dataset.template || '').replace('[remaining]', String(remaining));
        count.textContent = text;
        // Announce only when crossing into the last 20 characters (not on every key).
        const low = remaining <= 20;
        if (live && low !== wasLow) live.textContent = low ? text : '';
        wasLow = low;
      };
      message.addEventListener('input', update, { signal });
      update();
    }

    for (const key of ['email', 'name', 'message', 'send_on']) {
      this.#field(key)?.addEventListener('input', () => this.#setError(key, ''), { signal });
    }

    this.form = this.closest('form');
    this.form?.addEventListener('submit', () => this.#clearErrors(), { signal });
    this.form?.addEventListener('gift-card-recipient:errors', (event) => this.#showErrors(event.detail?.errors), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  #field(key) {
    return this.querySelector(`[data-recipient-field="${key}"]`);
  }

  #ymd(date) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  #sync(moveFocus) {
    const on = this.toggle.checked;
    this.fields.hidden = !on;
    this.fields.disabled = !on;
    this.toggle.setAttribute('aria-expanded', String(on));
    // Typed values stay, so ticking again restores them.
    if (on && moveFocus) this.#field('email')?.focus();
    if (!on) this.#clearErrors();
  }

  #setError(key, text) {
    const input = this.#field(key);
    const errorEl = input?.closest('.gift-recipient__field')?.querySelector('.gift-recipient__error');
    if (!input || !errorEl) return;
    errorEl.textContent = text || '';
    errorEl.hidden = !text;
    if (text) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  #clearErrors() {
    for (const key of ['email', 'name', 'message', 'send_on']) this.#setError(key, '');
  }

  #showErrors(errors) {
    if (!errors || typeof errors !== 'object') return;
    const fallback = { email: this.dataset.errorEmail, send_on: this.dataset.errorSendOn, message: this.dataset.errorMessage };
    let first = null;
    for (const key of ['email', 'name', 'message', 'send_on']) {
      if (!(key in errors)) continue;
      const value = errors[key];
      const text = Array.isArray(value) ? value.join(' ') : typeof value === 'string' ? value : fallback[key] || '';
      this.#setError(key, text);
      if (!first) first = this.#field(key);
    }
    first?.focus();
  }
}
if (!customElements.get('gift-card-recipient')) {
  customElements.define('gift-card-recipient', GiftCardRecipient);
}

/* --- Product recommendations (M3.4) ---
   v2 (RET-257): the fallback row under the product page when the vendor has no brand
   collection (More from the brand takes its place otherwise; the complementary pairs are
   Pairs well with in the buy box, assets/v2-product.js). Loads when the row comes near the
   viewport: related picks, then the same vendor, each rendered by the
   product-recommendations section. De-duplicated, the current product left out, sold-out
   cards used only to fill the row. Vendor cards in the current Scent move to the front, and
   every card with that scent links straight to its variant. fetchItems is shared with the
   cart drawer's Complete the set. */
class ProductRecommendations extends HTMLElement {
  /* Fetch a section render and return its [data-recommendation-item] nodes. A failed or
     non-OK request is an empty tier; it never throws. Shared with the cart upsell. */
  static async fetchItems(url, signal, selector = '[data-recommendation-item]') {
    if (!url) return [];
    try {
      const res = await fetch(url, { signal, headers: { Accept: 'text/html' } });
      if (!res.ok) return [];
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return [...doc.querySelectorAll(selector)];
    } catch (e) {
      return [];
    }
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-recommendations-grid]');
    if (!this.grid || this.started) return;
    this.abort?.abort();
    this.abort = new AbortController();
    if (!('IntersectionObserver' in window)) {
      this.#load();
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.#load();
    }, { rootMargin: '0px 0px 400px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abort?.abort();
    this.selectAbort?.abort();
  }

  async #load() {
    if (this.started) return;
    this.started = true;
    const { signal } = this.abort;
    const limit = parseInt(this.dataset.limit, 10) || 4;
    const currentId = this.dataset.productId;
    const [related, vendor] = await Promise.all([
      ProductRecommendations.fetchItems(this.dataset.relatedUrl, signal),
      ProductRecommendations.fetchItems(this.dataset.vendorUrl, signal),
    ]);
    if (signal.aborted) return;

    const scent = this.#currentScent();
    if (scent) {
      // Same-scent vendor cards first (the wash leads to the lotion in its scent).
      const hasScent = (item) => Object.prototype.hasOwnProperty.call(this.#scentMap(item), scent);
      vendor.sort((a, b) => Number(hasScent(b)) - Number(hasScent(a)));
    }

    const seen = new Set([currentId]);
    const picked = [];
    const spare = [];
    const add = (items, soldOutAsSpare, tier) => {
      for (const item of items) {
        const id = item.dataset.productId;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        // The source tier stays on the card so QA can tell which list supplied it.
        item.dataset.recTier = tier;
        if (soldOutAsSpare && item.querySelector('.product-card--sold-out')) spare.push(item);
        else picked.push(item);
      }
    };
    add(related, true, 'related');
    add(vendor, true, 'vendor');
    const items = picked.concat(spare).slice(0, limit);
    this.dataset.tierCounts = `related:${related.length} vendor:${vendor.length}`;

    if (!items.length) {
      // Nothing to show: no empty heading or reserved gap.
      (this.closest('.shopify-section') || this).remove();
      return;
    }
    this.grid.replaceChildren(...items.map((item) => document.importNode(item, true)));
    this.grid.classList.remove('product-recommendations__grid--loading');
    this.grid.setAttribute('aria-busy', 'false');
    this.#linkScent(scent);
    this.#followScent();
  }

  #scentMap(item) {
    const raw = item.querySelector('[data-scent-variants]')?.dataset.scentVariants;
    if (!raw) return {};
    try {
      return JSON.parse(raw) || {};
    } catch (e) {
      return {};
    }
  }

  /* v2: the Scent option is a chip group (product-option-selects); its checked radio is the scent. */
  #currentScent() {
    const checked = document.querySelector('product-form [data-option-group][data-option-name="scent"] [data-option-radio]:checked');
    return (checked && checked.value) || this.dataset.scent || '';
  }

  /* Point each card link at the variant in the current scent when it has one. */
  #linkScent(scent) {
    for (const card of this.grid.querySelectorAll('[data-scent-variants]')) {
      const id = scent ? this.#scentMap(card)[scent] : null;
      for (const link of card.querySelectorAll('.product-card__link, a.product-card__quick')) {
        const url = new URL(link.getAttribute('href'), window.location.origin);
        if (id) url.searchParams.set('variant', id);
        else url.searchParams.delete('variant');
        link.setAttribute('href', url.pathname + url.search);
      }
    }
  }

  #followScent() {
    this.selectAbort?.abort();
    this.selectAbort = new AbortController();
    document.addEventListener('variant:change', () => this.#linkScent(this.#currentScent()), { signal: this.selectAbort.signal });
  }
}
if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

/* --- Reveal-on-scroll (subtle fade + lift as sections enter the viewport) --- */
(function initReveal() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion || !('IntersectionObserver' in window)) return;

  // Only target sections inside the main content area. Header/footer groups stay put:
  // transforming a sticky element's ancestor breaks `position: sticky`.
  const main = document.getElementById('MainContent');
  if (!main) return;

  const targets = main.querySelectorAll(':scope > .shopify-section');
  const filtered = Array.from(targets).filter((el) => {
    // Don't fade the hero: it sits under the translucent header and any transform
    // would fight our negative-margin overlap trick.
    if (el.querySelector('.hero-banner')) return false;
    // M3.12: nor the product section. Its will-change: transform would make it the
    // containing block of the fixed sticky bar, and opacity 0 delays the main image paint.
    if (el.querySelector('.product-page')) return false;
    return true;
  });

  filtered.forEach((el) => el.classList.add('reveal'));

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal--visible');
          io.unobserve(entry.target);
        }
      }
    },
    { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
  );

  filtered.forEach((el) => io.observe(el));
})();

/* --- Header Group Height (exposed as --header-group-offset for hero overlap) ---
   Shopify renders each section in a `{% sections %}` group as its OWN sibling
   at the body level (with the group class applied to each). So we have to sum
   the heights of all elements matching `.shopify-section-group-header-group`
   rather than reading the offsetHeight of a single wrapper. */
(function initHeaderGroupHeight() {
  const root = document.documentElement;

  function update() {
    const groups = document.querySelectorAll('.shopify-section-group-header-group');
    if (!groups.length) return;
    // Measure from viewport 0 to the bottom of the last group element,
    // more accurate than summing offsetHeights (handles fixed/sticky children too).
    const lastBottom = groups[groups.length - 1].getBoundingClientRect().bottom;
    root.style.setProperty('--header-group-offset', `${Math.max(0, Math.round(lastBottom))}px`);
    // Header-only and announcement-only heights, so the hero can sit just
    // below the announcement bar while the header floats over its top.
    const headerEl = document.querySelector('.header');
    if (headerEl) root.style.setProperty('--header-offset', `${Math.round(headerEl.getBoundingClientRect().height)}px`);
    // Sum every announcement bar (RET-132 can stack more than one), not just the first:
    // a hidden/dismissed bar naturally contributes 0 via its [hidden] display:none.
    let annHeight = 0;
    document.querySelectorAll('.announcement-bar').forEach((el) => {
      annHeight += el.getBoundingClientRect().height;
    });
    root.style.setProperty('--announcement-offset', `${Math.round(annHeight)}px`);
    // Pinned-bar height only (RET-132), so the sticky header can dock directly
    // beneath whichever bars are actually pinned, rather than always at 0.
    let pinnedHeight = 0;
    document.querySelectorAll('.announcement-bar--pinned').forEach((el) => {
      pinnedHeight += el.getBoundingClientRect().height;
    });
    root.style.setProperty('--pinned-announcement-offset', `${Math.round(pinnedHeight)}px`);
  }

  update();

  // Re-measure on window resize + on load (images may have resized by then)
  let resizeRAF;
  const schedule = () => {
    cancelAnimationFrame(resizeRAF);
    resizeRAF = requestAnimationFrame(update);
  };
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);

  // Re-measure when the announcement bar is dismissed/restored
  if ('MutationObserver' in window) {
    const mo = new MutationObserver(schedule);
    document.querySelectorAll('.shopify-section-group-header-group').forEach((g) => {
      mo.observe(g, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'class', 'style'] });
    });
  }
})();

/* --- Cart page quantity steppers ---
   The +/- buttons on the /cart page had no handler (the drawer is the primary
   cart, but the page is still directly reachable). Wire them to adjust the
   matching quantity input and submit the cart form so line totals update. */
(function initCartPageQty() {
  const root = window.Shopify?.routes?.root || '/';

  async function changeLine(id, quantity) {
    const res = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id, quantity }),
    });
    if (!res.ok) throw new Error('Cart change failed');
    window.location.reload();
  }

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-cart-page-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.getAttribute('data-cart-page-remove'), 0).catch((err) => {
        console.error(err);
        window.location.href = remove.href;
      });
      return;
    }

    const minus = event.target.closest('[data-cart-qty-minus]');
    const plus = event.target.closest('[data-cart-qty-plus]');
    if (!minus && !plus) return;

    const button = minus || plus;
    const key = button.getAttribute(minus ? 'data-cart-qty-minus' : 'data-cart-qty-plus');
    const input = document.querySelector('[data-cart-qty-input="' + key + '"]');
    if (!input) return;

    event.preventDefault();
    const current = parseInt(input.value, 10) || 0;
    const next = minus ? Math.max(0, current - 1) : current + 1;
    if (next === current) return;
    changeLine(key, next).catch((err) => {
      console.error(err);
      input.value = next;
      const form = input.closest('form');
      if (form) form.submit();
    });
  });
})();


/* --- Transparent header over hero (solidifies once the hero scrolls past) --- */
(function initHeaderHeroOverlay() {
  const header = document.querySelector('.header');
  const hero = document.querySelector('.hero-banner');
  if (!header || !hero) return;
  function update() {
    const heroBottom = hero.getBoundingClientRect().bottom;
    const headerBottom = header.getBoundingClientRect().bottom;
    header.classList.toggle('is-over-hero', heroBottom > headerBottom);
  }
  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  window.addEventListener('load', update);
})();

/* --- Local actions (M1.18) ---
   Elements tagged data-local-action (call | directions | hours) with a
   data-local-action-placement publish one custom event per click, so a custom pixel
   (held for Millar, D10) or Clarity (D3) can record calls, directions and hours taps.
   The pixel clicked event does not carry data attributes, hence the custom event.
   Never calls preventDefault: the link still dials or opens maps. The document
   CustomEvent 'flush:local-action' is the test hook. */
(function initLocalActions() {
  const TYPES = ['call', 'directions', 'hours'];
  document.addEventListener('click', (event) => {
    const el = event.target.closest('[data-local-action]');
    if (!el) return;
    const type = el.getAttribute('data-local-action');
    if (!TYPES.includes(type)) return;
    const detail = {
      type,
      placement: (el.getAttribute('data-local-action-placement') || 'unknown').slice(0, 40),
      path: window.location.pathname,
    };
    document.dispatchEvent(new CustomEvent('flush:local-action', { detail }));
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('flush:local_action', detail);
      }
      if (typeof window.clarity === 'function') window.clarity('event', 'local_' + type);
    } catch (e) {
      /* never block the tap */
    }
  });
})();

/* --- Brand marquee: pause toggle (touch and keyboard users; hover and focus pause in CSS) --- */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-marquee-toggle]');
  if (!toggle) return;
  const root = toggle.closest('.brand-marquee');
  if (!root) return;
  const paused = root.classList.toggle('is-paused');
  toggle.setAttribute('aria-pressed', String(paused));
});
